
import Vue from 'vue';
import _ from 'lodash';
import { standardFunctions } from '@/helpers/standardFunctions';
import { FTProcess } from '@/models/FTProcess';
import { FTProcessRow } from '@/models/FTProcessRow';
import FtGoodsAutocomplete from './CFtGoodsAutocomplete.vue'
import { compareStringIgnoreCase } from '@/utils';
import { TranslationService } from '@/services/TranslationService';
import { goodsName } from '@/helpers/goodsName';

export default Vue.extend({
  components: {
    FtGoodsAutocomplete
  },
  props: {
    input: { type: Object, default: () => {} },
    data: { type: Object, default: () => {} },
    getVariable: { type: Function, default: () => () => {} },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => {} }
  },
  data(): {
    processes: FTProcess[],
    tempInputQtyVariable: number[],
    tempOutputQtyVariable: number[],
    wipFilter: any

  } {
    return {
      processes: this.getVariable(this.input.config.processesVariable),
      tempInputQtyVariable: [],
      tempOutputQtyVariable: [],
      wipFilter: { status: ['WIP'] }
    }
  },
  computed: {
    enablePcsToKgInOutputs: function(): boolean {
      return ['Cup Forming', 'Narrow Slitting', 'Sachet Forming', 'Packing', 'Trading'].includes(this.process.type);
    },
    process: function(): FTProcess {
      return this.getVariable(this.input.variable);
    },
    goods: function(): any[] {
      return this.getVariable(this.input.config.goodsVariable) || [];
    },
    orderDetails: function(): any {
      return this.getVariable(this.input.config.orderVariable) || {};
    },
    totalTargetQuantity: function(): number {
      let totalTargetQuantity = 0;
      _.map(this.process && this.process.inputs, (input) => {
        totalTargetQuantity = totalTargetQuantity + _.get(input, 'report.quantity', 0) - _.get(input, 'report.runningWaste', 0) - _.get(input, 'report.materialReturn', 0);
      });
      return _.floor(totalTargetQuantity,3);
    },
    totalOutputQuantity: function(): number {
      let totalOutputQuantity = 0;
      _.map(this.process && this.process.outputs, (output) => {
        totalOutputQuantity = totalOutputQuantity + _.get(output, 'report.quantity', 0) - _.get(output, 'report.runningWaste', 0) - _.get(output, 'report.materialReturn', 0);
      });
      return totalOutputQuantity;
    },
    wipItems: function(): any[] {
      const wipItems = this.getVariable(this.input.config.wipVariable) || [];
      return wipItems.map((selectedGood: any) => {
        return {
          ...selectedGood,
          __text: goodsName(selectedGood, this.processes)
        }
      })
    }
  },
  watch: {
    totalTargetQuantity() {
      this.updateOutputReportValues();
    }
  },
  mounted() {
    this.$watch(
      () => this.getVariable(this.input.variable) && this.getVariable(this.input.variable)._key,
      (processKey) => {
        if(this.input.config.resetData) {
          let process = this.processes[processKey];
          process = this.initialiseProcess(processKey);
          const { prefix, key } = this.parent._getPrefixAndKey(this.input.variable);
          this.$set(this.parent._getVariable(prefix), key, { ...process, _key: processKey });
        }
        _.get(this.process, 'outputs', []).forEach((output, index) => {
          const goodsObject = this.parent.getGoodFromId(output._id);
          if(this.enablePcsToKgInOutputs) {
            this.$set(this.tempOutputQtyVariable, index, standardFunctions['kgToPcs'](_.get(output, 'report.quantity',0) , goodsObject));
          } else {
            let quantity = _.get(output,'report.quantity', 0);
            if(compareStringIgnoreCase(_.get(goodsObject, 'rawMaterialUnit', ''),'SQM')) {
              quantity = standardFunctions['kgToSQM'](quantity, goodsObject);
            }
            this.$set(this.tempOutputQtyVariable, index, quantity);
          }
        });
        _.get(this.process, 'inputs', []).forEach((input, index) => {
          const goodsObject = this.parent.getGoodFromId(this.process.inputs[index]._id);
          if(this.enablePcsToKgInInputs(input)) {
            this.$set(this.tempInputQtyVariable, index, standardFunctions['kgToPcs'](_.get(input, 'report.quantity',0) , goodsObject));
          } else {
            let quantity = _.get(input,'report.quantity', 0);
            if(compareStringIgnoreCase(_.get(goodsObject, 'rawMaterialUnit', ''),'SQM')) {
              quantity = standardFunctions['kgToSQM'](quantity, goodsObject);
            }
            this.$set(this.tempInputQtyVariable, index, quantity);
          }
        });
      },
      { immediate: true }
    );
  },
  methods: {
    initialiseProcess: function(processKey: any) {
      const process = this.processes[processKey];
      _.map(process && process.inputs, (input) => {
        if (!input.report) { 
          this.$set(input, 'report', { quantity: 0 });
        }
      });
      _.map(process && process.outputs, (output) => {
        if (!output.report) { 
          this.$set(output, 'report', { quantity: 0 });
        }
      });
      return process;
    },
    getGoodDataWithFilter: function(goodId: number): any {
      const selectedGood = this.goods.find((good: any) => good._id === goodId);
      return goodsName(selectedGood, this.processes);
    },
    getGoodUnit: function(goodId: number): string {
      const goodsObject = this.parent.getGoodFromId(goodId);
      return _.get(goodsObject, 'rawMaterialUnit', 'Kg');
    },
    updateInputValues(key: number) {
      const processInputRow = this.process.inputs[key];
      const goodsObject = this.parent.getGoodFromId(processInputRow._id);
      if(this.enablePcsToKgInInputs(processInputRow)) {
        const updatedQuantity = standardFunctions['pcsToKg'](this.tempInputQtyVariable[key], goodsObject);
        _.set(processInputRow, `report.quantity`, updatedQuantity);
      } else {
        let updatedQuantity = this.tempInputQtyVariable[key];
        if(compareStringIgnoreCase(_.get(goodsObject, 'rawMaterialUnit', ''),'SQM')) {
          updatedQuantity = standardFunctions['sqmToKg'](updatedQuantity, goodsObject);
        }
        _.set(processInputRow, `report.quantity`, updatedQuantity);
      }
      if(this.onChange) this.onChange(this.input.onChange,{ selectedObject: this.getVariable(this.input.variable)});
    },
    updateOutputValues(key: number) {
      const goodsObject = this.parent.getGoodFromId(this.process.outputs[key]._id);
      if(this.enablePcsToKgInOutputs) {
        const updatedQuantity = standardFunctions['pcsToKg'](this.tempOutputQtyVariable[key], goodsObject);
        _.set(this.process, `outputs.${key}.report.quantity`, updatedQuantity);
      } else {
        let updatedQuantity = this.tempOutputQtyVariable[key];
        if(compareStringIgnoreCase(_.get(goodsObject, 'rawMaterialUnit', ''),'SQM')) {
          updatedQuantity = standardFunctions['sqmToKg'](updatedQuantity, goodsObject);
        }
        _.set(this.process, `outputs.${key}.report.quantity`, updatedQuantity);
      }
      this.updateOutputReportValues();
      if(this.onChange) this.onChange(this.input.onChange,{ selectedObject: this.getVariable(this.input.variable)});
    },
    updateOutputReportValues() {
      this.process.outputs = _.map(this.process.outputs, (output) => {
        if(output.report) {
          output.remainingQuantity = Number((this.totalTargetQuantity - this.totalOutputQuantity).toFixed(3));
          output.report.varience = Number((this.totalTargetQuantity - this.totalOutputQuantity).toFixed(3));
        }
        return output;
      });
    },
    enablePcsToKgInInputs(processInputRow: FTProcessRow): boolean {
      if(this.process.type !== 'Packing') return false;
      if(processInputRow.goodsType !== 'product') return false;
      const productLine = _.get(this.orderDetails, 'product.productLine');
      if(['Wrap Paper', 'Cake Board', 'Corrugated Paper', 'Tray'].includes(productLine)) return false;
      return true;
    },
    addWIP(inputItem: FTProcessRow, key: number) {
      let newInputs: any[] = [];
      const newInputKey = key + 1;
      for(let i = 0; i <= this.process.inputs.length; i++) {
        if(i < newInputKey) {
          newInputs[i] = this.process.inputs[i];
        } else if ( i > newInputKey) {
          newInputs[i] = this.process.inputs[i-1];
        } else if ( i === newInputKey) {
          newInputs[i] = { ...inputItem, key, quantity: 0, _id: '', report: { quantity: 0, replacing: inputItem._id } };
        }
      }
      this.process.inputs = newInputs;
      if(this.onChange) this.onChange(this.input.onChange,{ selectedObject: this.getVariable(this.input.variable)});
    },
    removeWIP(indexToDelete: number) {
      Vue.delete(this.process.inputs, indexToDelete);
      if(this.onChange) this.onChange(this.input.onChange,{ selectedObject: this.getVariable(this.input.variable)});
    },
    translate: function(text: string): string {
      if(!text) return '';
      return TranslationService.translate(text);
    }
  }
})
